import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import styles from './NavDrawer.module.scss';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export interface INavDrawerProps {
  open: boolean;
  onClose: () => void;
  returnFocusRef?: React.RefObject<HTMLElement>;
  footer?: React.ReactNode;
}

export default function NavDrawer({
  open,
  onClose,
  returnFocusRef,
  footer,
}: INavDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const titleId = useId();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (open) onClose();
    }
  }, [location.pathname, open, onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const { body } = document;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const panel = panelRef.current;
    const focusables = panel
      ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
          el => !el.hasAttribute('disabled') && el.tabIndex !== -1,
        )
      : [];
    focusables[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
      returnFocusRef?.current?.focus();
    };
  }, [open, onClose, returnFocusRef]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className={styles.root}>
      <button
        type="button"
        className={styles.scrim}
        aria-label="Close menu"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className={styles.visuallyHidden}>
          Navigation
        </h2>
        <Sidebar
          showCloseButton
          onClose={onClose}
          onNavigate={onClose}
          footer={footer}
        />
      </div>
    </div>,
    document.body,
  );
}
